'use client'

import { Input } from '@headlessui/react'
import clsx from 'clsx'
import type { ControlledFormField, FormFieldCommonProps } from 'lib/types/Form'
import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Controller } from 'react-hook-form'
import InputAddon, { InputAddonProps } from '../InputAddon/InputAddon'
import InputError from '../InputError/InputError'
import InputHint from '../InputHint/InputHint'
import InputLabel from '../InputLabel/InputLabel'

interface CardExpirationInputStandaloneProps
  extends InputAddonProps,
    Omit<FormFieldCommonProps, 'onChange'> {
  /**
   * The placeholder label for the input
   */
  placeholder?: string
  /**
   * Callback that runs when the input loses focus
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  /**
   * Callback that runs when the input value changes
   */
  onChange?: (data?: string | number, fieldName?: string) => void
}

interface CardExpirationInputProps
  extends ControlledFormField,
    CardExpirationInputStandaloneProps {}

// Standalone component that can be used outside react-hook-form's context
export const CardExpirationInputStandalone = forwardRef<
  HTMLInputElement,
  CardExpirationInputStandaloneProps
>(function CardExpirationInputStandaloneWithRef(
  {
    label,
    name,
    id = name,
    className,
    error,
    onBlur,
    onChange,
    defaultValue = '',
    disabled,
    hint,
    placeholder,
    ...props
  }: CardExpirationInputStandaloneProps,
  ref,
) {
  const removeNonNumbers = (value: string | number) => {
    return String(value).replaceAll(/[^\d]+/g, '')
  }

  const formatValue = useCallback(
    (valueString: string | number = defaultValue) => {
      const digits = removeNonNumbers(valueString)
      if (!digits) return ''

      let month: string
      let year: string

      if (digits.length === 1) {
        // Only one digit so far → single‐digit month
        month = digits
        year = ''
      } else {
        const firstTwo = digits.slice(0, 2)
        // If firstTwo is 01–12, use that as month; otherwise fallback to first digit
        if (Number(firstTwo) >= 1 && Number(firstTwo) <= 12) {
          month = firstTwo
          year = digits.slice(2, 4)
        } else {
          month = digits.slice(0, 1)
          year = digits.slice(1, 3)
        }
      }

      // As soon as user types a 3rd digit, pad single‐digit months
      if (digits.length > 2 && month.length === 1) {
        month = `0${month}`
      }

      // Build output: insert slash after two‐digit month
      let out = month
      if (month.length === 2) {
        out += '/'
      }
      if (year) {
        out += year
      }

      return out
    },
    [defaultValue],
  )

  const [isEnabled, setIsEnabled] = useState(
    'conditionalEdit' in props && props.conditionalEdit ? false : !disabled,
  )
  const [formattedValue, setFormattedValue] = useState(
    formatValue(defaultValue),
  )
  const onInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      // Format updated input if user didn't do a deletion
      const newValue =
        String(value).length < formattedValue.length
          ? value
          : formatValue(value)
      setFormattedValue(newValue)
      onChange?.(newValue, name)
    },
    [onChange, name, formatValue, formattedValue],
  )

  useEffect(() => {
    setFormattedValue(formatValue(defaultValue))
  }, [defaultValue, formatValue])
  return (
    <div className={clsx(`text-input`, className)}>
      <div className="flex items-baseline justify-between">
        {label && <InputLabel htmlFor={id}>{label}</InputLabel>}
        <InputAddon
          {...props}
          {...('conditionalEdit' in props && props.conditionalEdit
            ? { onEdit: setIsEnabled }
            : {})}
        />
      </div>
      <Input
        className={clsx(
          'w-full type-body-8 rounded-8 h-12 flex items-center px-4 my-2 transition-shadow lg:h-14',
          'disabled:bg-neutral-200 disabled:text-neutral-600 disabled:shadow-input-disabled disabled:cursor-not-allowed',
          'focus:shadow-input-focus focus:outline-none',
          'hover:shadow-input-active',
          error ? '!shadow-input-error' : 'shadow-input',
        )}
        ref={ref}
        name={name}
        id={id}
        placeholder={placeholder}
        onBlur={onBlur}
        onChange={onInputChange}
        disabled={!isEnabled}
        value={formattedValue}
      />
      <InputHint>{hint}</InputHint>
      <InputError>{error}</InputError>
    </div>
  )
})

// Controlled form element that is automatically registered to react-hook-form
export default function CardExpirationInput({
  name,
  control,
  rules,
  defaultValue,
  onBlur,
  onChange,
  disabled,
  ...props
}: CardExpirationInputProps) {
  return (
    <Controller
      name={name || ''}
      defaultValue={defaultValue}
      control={control}
      rules={rules}
      render={({
        field: { onChange: fieldOnChange, ...field },
        fieldState,
      }) => {
        const onInputChange = (data?: number | string) => {
          fieldOnChange?.(data)
          onChange?.(data, name)
        }

        return (
          <CardExpirationInputStandalone
            {...props}
            {...field}
            defaultValue={defaultValue}
            disabled={disabled}
            onBlur={onBlur}
            onChange={onInputChange}
            error={fieldState.error?.message}
          />
        )
      }}
    />
  )
}
